import { NavLink, Outlet, useNavigate } from 'react-router';
import { IconLogout, IconUser } from '@tabler/icons-react';
import {
  Badge,
  Footer,
  LanguageSelect,
  Logo,
  Menu,
  cn,
  useLocale,
  PRESS,
} from 'escrowbuild-ui';

import { useT } from '@/i18n';
import { PORTAL_NAV } from './nav';
import { usePageTitle } from './usePageTitle';

/**
 * The investor the portal is signed in as.
 *
 * One of the platform's real investors, so the participation totals the portal
 * shows are the same numbers the internal screens show for the same person. A
 * demo where the two disagree undermines the product's whole claim, which is
 * that the investor sees a verified copy of the truth.
 */
const SESSION = { id: 'prado', name: 'Helena Prado', initials: 'HP' };

/**
 * The investor portal's shell.
 *
 * Deliberately a different shell from the platform's, not a variant of it. §12
 * asks for an area that is secure and visually well presented and that does not
 * reproduce an internal system; handing the investor the same sidebar and
 * controls as the builder's team would contradict the product's own permission
 * model in the most visible way available.
 *
 * What the two shells share is the footer, because the vendor credit belongs to
 * both audiences — it is a vendor credit, not a product feature.
 */
export function PortalShell() {
  const navigate = useNavigate();
  const { locale, setLocale } = useLocale();
  const t = useT();
  usePageTitle();

  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-body">
      <header className="sticky top-0 z-[var(--z-sticky)] border-b border-line-subtle bg-surface/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[var(--container-wide)] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            {/*
              `Logo` renders its own anchor, so there is no second one wrapped
              around it — a nested anchor is invalid, and browsers repair it by
              splitting the element in two.
            */}
            <Logo href="/portal" name="escrowbuild.io" tagline={t('shell.portal.tagline')} size={28} />
            <Badge tone="info" variant="subtle" size="sm">
              {t('shell.portal.access')}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {/*
              The portal's bar carries the language control in its own shape — a
              label-height pale control rather than the header's square one —
              and the shape is the whole difference between the two: the flag,
              the menu and the two accessible names come from the one picker the
              platform, the portal and the sign-in stage all use.
            */}
            <LanguageSelect
              locale={locale}
              onLocaleChange={setLocale}
              className="inline-flex w-auto gap-2 border-line px-2.5"
            />

            <Menu
              label={t('nav.account')}
              placement="bottom-end"
              items={[
                {
                  id: 'portal-identity',
                  label: `${SESSION.name} · ${t('shell.portal.role')}`,
                  icon: <IconUser size={14} aria-hidden="true" />,
                },
                {
                  id: 'portal-signout',
                  label: t('nav.signOut'),
                  icon: <IconLogout size={14} aria-hidden="true" />,
                  onSelect: () => navigate('/'),
                },
              ]}
              trigger={
                <button
                  type="button"
                  aria-label={t('shell.portal.accountNamed', { name: SESSION.name })}
                  className={cn(
                    'relative inline-flex h-11 items-center gap-2 rounded-xs border border-line-strong bg-surface px-2.5 text-heading shadow-hard-sm',
                    PRESS,
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                >
                  {/* See the note in AppShell: `text-primary` on its own
                      subtle tint measures 4.29:1 and fails AA at this size. */}
                  <span className="grid size-6 place-items-center rounded-pill bg-primary-subtle font-display text-2xs font-semibold text-heading">
                    {SESSION.initials}
                  </span>
                  <span className="hidden text-sm sm:inline">{SESSION.name}</span>
                </button>
              }
            />
          </div>
        </div>

        <nav
          aria-label={t('nav.section.portal')}
          className="mx-auto w-full max-w-[var(--container-wide)] px-4 sm:px-6"
        >
          <ul className="flex list-none gap-1 overflow-x-auto p-0">
            {PORTAL_NAV.map((item) => (
              <li key={item.path} className="shrink-0">
                <NavLink
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'relative inline-flex min-h-11 items-center px-3 text-sm font-medium no-underline',
                      'transition-colors duration-[var(--dur-fast)]',
                      PRESS,
                      isActive ? 'text-heading' : 'text-muted hover:text-heading',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {t(item.labelKey)}
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute inset-x-2 bottom-0 h-0.5 rounded-pill',
                          isActive ? 'bg-primary' : 'bg-transparent',
                        )}
                      />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <div className="mx-auto w-full max-w-[var(--container-wide)] flex-1 px-4 py-8 sm:px-6">
        <Outlet />
      </div>

      <Footer />
    </div>
  );
}
